import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { z } from "zod";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextField,
  MDSTextFieldVariant,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import type {
  MemAgentEmailAllowlistEntry,
  UpdateMemAgentEmailSenderAllowlistArgs,
} from "@/store/mem-agent/mem-agent-store";

export interface SettingsEmailSenderAllowlistSectionProps {
  emailChannelAddress?: string;
  protectedEmailSenderAllowlistEntry?: MemAgentEmailAllowlistEntry;
  emailSenderAllowlist: MemAgentEmailAllowlistEntry[];
  isSavingEmailSenderAllowlist: boolean;
  canSaveEmailSenderAllowlist?: boolean;
  onSaveEmailSenderAllowlist: (
    args: UpdateMemAgentEmailSenderAllowlistArgs
  ) => Promise<boolean | void> | boolean | void;
}

const emailAllowlistEmailSchema = z.string().email();

const isSameEmailSenderAllowlistEntry = (
  entryA: MemAgentEmailAllowlistEntry,
  entryB: MemAgentEmailAllowlistEntry
): boolean =>
  entryA.kind === entryB.kind && entryA.value.toLowerCase() === entryB.value.toLowerCase();

const normalizeEmailSenderAllowlistValue = (
  rawValue: string
): {
  entry: MemAgentEmailAllowlistEntry | null;
  errorMessage: string | null;
} => {
  const trimmedValue = rawValue.trim();
  if (!trimmedValue) {
    return { entry: null, errorMessage: "Enter an email address." };
  }

  const value = trimmedValue.toLowerCase();
  const isValid = emailAllowlistEmailSchema.safeParse(value).success;

  if (!isValid) {
    return { entry: null, errorMessage: `Invalid email: ${trimmedValue}` };
  }

  return { entry: { kind: "email", value }, errorMessage: null };
};

/**
 * Renders the shared email sender allowlist editor used by Mem Agent and Reminders settings.
 */
export const SettingsEmailSenderAllowlistSection: FC<SettingsEmailSenderAllowlistSectionProps> = ({
  emailChannelAddress,
  protectedEmailSenderAllowlistEntry,
  emailSenderAllowlist,
  isSavingEmailSenderAllowlist,
  canSaveEmailSenderAllowlist = true,
  onSaveEmailSenderAllowlist,
}) => {
  const textFieldWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isAddingEmailSender, setIsAddingEmailSender] = useState(false);
  const [newEmailSenderAllowlistValue, setNewEmailSenderAllowlistValue] = useState("");
  const [emailSenderAllowlistError, setEmailSenderAllowlistError] = useState<string | null>(null);
  const displayEmailChannelAddress = emailChannelAddress ?? "Allowed email senders";
  // The logged-in account email is always accepted by Mem Agent, but it is not part of the mutable
  // server allowlist and should not get delete controls.
  const mutableEmailSenderAllowlist = useMemo(() => {
    if (!protectedEmailSenderAllowlistEntry) return emailSenderAllowlist;

    return emailSenderAllowlist.filter(
      entry => !isSameEmailSenderAllowlistEntry(entry, protectedEmailSenderAllowlistEntry)
    );
  }, [emailSenderAllowlist, protectedEmailSenderAllowlistEntry]);
  const emailSenderRows = useMemo(() => {
    return [
      ...(protectedEmailSenderAllowlistEntry
        ? [{ entry: protectedEmailSenderAllowlistEntry, isRemovable: false }]
        : []),
      ...mutableEmailSenderAllowlist.map(entry => ({ entry, isRemovable: true })),
    ];
  }, [mutableEmailSenderAllowlist, protectedEmailSenderAllowlistEntry]);
  const hasExpandedEmailAllowlistContent = emailSenderRows.length > 0 || isAddingEmailSender;

  useEffect(() => {
    if (!isAddingEmailSender) return;

    const input = textFieldWrapperRef.current?.querySelector("input");
    input?.focus();
  }, [isAddingEmailSender]);

  const addEmailSenderAllowlistEntry = useCallback(async () => {
    const result = normalizeEmailSenderAllowlistValue(newEmailSenderAllowlistValue);
    if (!result.entry) {
      setEmailSenderAllowlistError(result.errorMessage);
      return;
    }

    const entry = result.entry;
    const isDuplicate = emailSenderRows.some(
      row => isSameEmailSenderAllowlistEntry(row.entry, entry)
    );
    if (isDuplicate) {
      setEmailSenderAllowlistError("This sender is already allowed.");
      return;
    }

    const saveResult = await onSaveEmailSenderAllowlist({
      emailSenderAllowlist: [...mutableEmailSenderAllowlist, entry],
    });
    if (saveResult === false) return;

    setNewEmailSenderAllowlistValue("");
    setEmailSenderAllowlistError(null);
    setIsAddingEmailSender(false);
  }, [
    emailSenderRows,
    mutableEmailSenderAllowlist,
    newEmailSenderAllowlistValue,
    onSaveEmailSenderAllowlist,
  ]);

  const deleteEmailSenderAllowlistEntry = useCallback(
    (entryToDelete: MemAgentEmailAllowlistEntry) => {
      onSaveEmailSenderAllowlist({
        emailSenderAllowlist: mutableEmailSenderAllowlist.filter(
          entry => !isSameEmailSenderAllowlistEntry(entry, entryToDelete)
        ),
      });
      setEmailSenderAllowlistError(null);
    },
    [mutableEmailSenderAllowlist, onSaveEmailSenderAllowlist]
  );

  const cancelAddingEmailSender = useCallback(() => {
    setNewEmailSenderAllowlistValue("");
    setEmailSenderAllowlistError(null);
    setIsAddingEmailSender(false);
  }, []);

  const openEmailSenderForm = useCallback(() => {
    setIsAddingEmailSender(true);
  }, []);

  return (
    <StyledEmailAllowlistRow aria-label="Email">
      <StyledEmailAllowlistPrimaryRow>
        <StyledEmailAllowlistLeft>
          <StyledAllowlistIconWrapper aria-hidden="true">
            <MDSIcon
              kind={MDSIconKind.Envelope}
              color={MDSIconColor.Primary}
              size={16}
              boxSize={16}
            />
          </StyledAllowlistIconWrapper>
          <StyledEmailAllowlistText>
            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
              Email
            </MDSText>
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              Email Mem Agent at {displayEmailChannelAddress}
            </MDSText>
          </StyledEmailAllowlistText>
        </StyledEmailAllowlistLeft>
        {!isAddingEmailSender ? (
          <MDSButton
            variant={MDSButtonVariant.Default}
            onPress={openEmailSenderForm}
            isDisabled={!canSaveEmailSenderAllowlist || isSavingEmailSenderAllowlist}
          >
            Connect another email
          </MDSButton>
        ) : null}
      </StyledEmailAllowlistPrimaryRow>

      {hasExpandedEmailAllowlistContent ? (
        <StyledEmailAllowlistDivider data-mds-name="email-allowlist-divider" />
      ) : null}

      <StyledEmailAllowlistExpandedContent>
        {emailSenderRows.length > 0 ? (
          <>
            <StyledEmailSenderList>
              {emailSenderRows.map(({ entry, isRemovable }) => (
                <StyledEmailSenderRow key={`${entry.kind}:${entry.value}`}>
                  <StyledEmailSenderValue>
                    <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                      {entry.value}
                    </MDSText>
                    <MDSIcon
                      kind={MDSIconKind.CheckCircleSolid}
                      color={MDSIconColor.Positive}
                      size={16}
                      boxSize={16}
                    />
                  </StyledEmailSenderValue>
                  {isRemovable ? (
                    <StyledEmailSenderDeleteButton
                      className="email-sender-delete-button"
                      onClick={() => deleteEmailSenderAllowlistEntry(entry)}
                      aria-label={`Delete ${entry.value}`}
                      iconKind={MDSIconKind.Trash}
                      size={MDSIconButtonSize.XSmall}
                      isDisabled={!canSaveEmailSenderAllowlist || isSavingEmailSenderAllowlist}
                    />
                  ) : null}
                </StyledEmailSenderRow>
              ))}
            </StyledEmailSenderList>
            {isAddingEmailSender ? (
              <StyledEmailAllowlistFullWidthDivider data-mds-name="email-allowlist-divider" />
            ) : null}
          </>
        ) : null}

        {isAddingEmailSender ? (
          <StyledEmailSenderAllowlistForm
            onSubmit={event => {
              event.preventDefault();
              void addEmailSenderAllowlistEntry();
            }}
          >
            <StyledEmailSenderAllowlistTitle>
              <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                Connect another email
              </MDSText>
            </StyledEmailSenderAllowlistTitle>
            <StyledEmailSenderAllowlistTextFieldWrapper ref={textFieldWrapperRef}>
              <StyledEmailSenderAllowlistTextField
                aria-label="Email sender allowlist value"
                value={newEmailSenderAllowlistValue}
                onChange={value => {
                  setNewEmailSenderAllowlistValue(value);
                  setEmailSenderAllowlistError(null);
                }}
                placeholder="Enter an email"
                variant={MDSTextFieldVariant.Default}
                isDisabled={isSavingEmailSenderAllowlist}
              />
            </StyledEmailSenderAllowlistTextFieldWrapper>
            {emailSenderAllowlistError ? (
              <StyledEmailSenderAllowlistError
                variant={MDSTextVariant.TextXs}
                color={MDSTextColor.Danger}
              >
                {emailSenderAllowlistError}
              </StyledEmailSenderAllowlistError>
            ) : null}
            <StyledEmailSenderAllowlistActions>
              <MDSButton
                type="button"
                variant={MDSButtonVariant.Default}
                onPress={cancelAddingEmailSender}
                isDisabled={isSavingEmailSenderAllowlist}
              >
                Cancel
              </MDSButton>
              <MDSButton
                type="submit"
                variant={MDSButtonVariant.Contrast}
                isLoading={isSavingEmailSenderAllowlist}
                isDisabled={!canSaveEmailSenderAllowlist || !newEmailSenderAllowlistValue.trim()}
              >
                Connect
              </MDSButton>
            </StyledEmailSenderAllowlistActions>
          </StyledEmailSenderAllowlistForm>
        ) : null}
      </StyledEmailAllowlistExpandedContent>
    </StyledEmailAllowlistRow>
  );
};

const StyledEmailAllowlistRow = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
  box-sizing: border-box;
  width: 100%;
  padding: 20px 20px 20px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.surface.pane};
`;

const StyledEmailAllowlistPrimaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  height: 40px;
  width: 100%;

  @container mds-panel (max-width: 600px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const StyledEmailAllowlistLeft = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledAllowlistIconWrapper = styled.div`
  display: flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const StyledEmailAllowlistText = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledEmailAllowlistDivider = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
  width: 100%;
  margin: 12px 0;

  &::before {
    content: "";
    display: block;
    height: 1px;
    width: 100%;
    background: ${({ theme }) => theme.colors.border.default};
  }
`;

const StyledEmailAllowlistFullWidthDivider = styled(StyledEmailAllowlistDivider)`
  margin-left: -40px;
  width: calc(100% + 40px);

  @container mds-panel (max-width: 520px) {
    margin-left: 0;
    width: 100%;
  }
`;

const StyledEmailAllowlistExpandedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-left: 40px;

  @container mds-panel (max-width: 520px) {
    padding-left: 0;
  }
`;

const StyledEmailSenderList = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledEmailSenderDeleteButton = styled(MDSIconButton)`
  opacity: 0;
  transition: opacity 120ms ease;
`;

const StyledEmailSenderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
  height: 40px;

  &:hover .email-sender-delete-button,
  &:focus-within .email-sender-delete-button {
    opacity: 1;
  }
`;

const StyledEmailSenderValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const StyledEmailSenderAllowlistForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const StyledEmailSenderAllowlistTitle = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  margin-bottom: 16px;
`;

const StyledEmailSenderAllowlistTextFieldWrapper = styled.div`
  min-width: 0;
  margin-bottom: 16px;
`;

const StyledEmailSenderAllowlistTextField = styled(MDSTextField)`
  min-width: 0;
`;

const StyledEmailSenderAllowlistError = styled(MDSText)`
  padding-left: ${({ theme }) => theme.spacing[1]};
`;

const StyledEmailSenderAllowlistActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};

  @container mds-panel (max-width: 520px) {
    flex-direction: column-reverse;
  }
`;
